import {
  forwardRef,
  type AriaAttributes,
  type ReactElement,
  type ComponentPropsWithoutRef,
} from "react";
import cx from "classnames";
import styles from "./FieldMessage.module.scss";
import { Icon } from "../Icon";
import Error from "@servicetitan/hammer-icon/mdi/round/warning.svg";

/**
 * Props for the FieldMessage component
 * @extends ComponentPropsWithoutRef<"div">
 */
export type FieldMessageProps = ComponentPropsWithoutRef<"div"> & {
  /**
   * Unique identifier for the field message container.
   */
  id?: string;
  /**
   * Hint text or element to display when there's no error.
   */
  hint?: ReactElement | string;
  /**
   * Description text or element to display below the main message.
   */
  description?: ReactElement | string;
  /**
   * Error message or element to display when there's an error.
   */
  errorMessage?: ReactElement | string;
  /**
   * aria-live attribute value for the error message.
   * @default assertive
   * @remarks Controls how screen readers announce error messages. "assertive" interrupts current speech, "polite" waits for current speech to finish.
   * @link https://www.w3.org/WAI/tutorials/forms/notifications/
   */
  errorAriaLive?: AriaAttributes["aria-live"];
};

/**
 * FieldMessage component for displaying hints, descriptions, and error messages for form fields.
 *
 * Features:
 * - Displays hint text when no error is present
 * - Shows error messages with warning icon when errors occur
 * - Supports both string and React element content
 * - Configurable aria-live behavior for screen readers
 * - Optional description text for additional context
 * - Accessible with proper ARIA attributes
 * - Visual error indicators with warning icons
 * - Conditional rendering based on error state
 * - Supports layout utilities for positioning and spacing
 *
 * @example
 * <FieldMessage
 *   hint="Enter your email address"
 *   description="We'll use this to send you updates"
 *   errorMessage="Please enter a valid email address"
 * />
 */
export const FieldMessage = forwardRef<HTMLDivElement, FieldMessageProps>(
  (props, ref) => {
    const {
      id,
      hint,
      description,
      errorAriaLive = "assertive",
      errorMessage,
      className,
      ...rest
    } = props;

    return (
      <div
        {...rest}
        className={cx(styles["messages"], className)}
        ref={ref}
        id={id}
      >
        {errorMessage ? (
          <div className={styles["error-container"]}>
            <Icon
              inherit
              className={styles["error-icon"]}
              aria-hidden
              svg={Error}
            />
            <span className={styles["error-message"]} aria-live={errorAriaLive}>
              {errorMessage}
            </span>
          </div>
        ) : (
          (hint ?? null)
        )}
        {description ? (
          <div className={styles["description-container"]}>
            {description ? <span>{description}</span> : null}
          </div>
        ) : null}
      </div>
    );
  },
);

FieldMessage.displayName = "FieldMessage";
